import React, { useState } from "react";
import { Outlet, Link } from "react-router-dom";
import ConsentModal from "../components/Consent";
import { useAuth } from "../components/TokenHandler";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import HomeIcon from "@mui/icons-material/Home";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import MonthlyCheckinRunReport from "../components/MonthlyCheckinRunReport";
import OptOutReportModal from "../components/OptOutReportModal";
import UploadSpreadsheetModal from "../components/UploadSpreadsheetModal";
import LogModal from "../components/LogAndRollBack";
import ProcessChanges from "../components/ProcessChanges";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import DashboardIcon from "@mui/icons-material/Dashboard";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import BlockIcon from "@mui/icons-material/Block";

import PatientConsentStatsModal from "../consent/modals/PatientConsentStatsModal";
import ExternalPatientConsentDashboardModal from "../consent/modals/ExternalPatientConsentDashboardModal";
import BatchUploadModal from "../consent/modals/BatchUploadModal";
import PatientIdLookupModal from "../consent/modals/PatientIdLookupModal";
import ExclusionsModal from "../components/ExclusionsModal";

const Layout = ({ selectedAction, setSelectedAction }) => {
    const { keycloak: kc, roles } = useAuth();

    const pageTitleMap = {
        "Monthly check in": "Monthly Check in Management System",
        "Opt out": "Opt Out Management System",
        Consent: "Consent Management System",
        "Facility Management": "Facility Service Management System",
    };

    const handleLogout = () => {
        const rootUrl = window.location.origin;
        kc.logout({ redirectUri: rootUrl });
    };

    const [openMontlyReportModal, setOpenMonthlyReportModal] = useState(false);
    const [openOptOutReportModal, setOpenOptOutReportModal] = useState(false);

    const [consentOpen, setConsentOpen] = useState(false);
    const [consentMode, setConsentMode] = useState("obtain");
    const [consentContext, setConsentContext] = useState(null);

    const openConsentModal = (mode, ctx = null) => {
        setConsentMode(mode);
        setConsentContext(ctx);
        setConsentOpen(true);
    };

    const closeConsentModal = () => {
        setConsentOpen(false);
        setConsentContext(null);
    };

    const [openUploadModal, setOpenUploadModal] = useState(false);
    const [openProcessChanges, setOpenProcessChanges] = useState(false);
    const [showLog, setShowLog] = useState(false);
    const [openExclusions, setOpenExclusions] = useState(false);
    const [openPatientConsentStats, setOpenPatientConsentStats] = useState(false);
    const [openExternalConsentDashboard, setOpenExternalConsentDashboard] = useState(false);
    const [openBatchUpload, setOpenBatchUpload] = useState(false);
    const [openPatientIdLookup, setOpenPatientIdLookup] = useState(false);

    const canUseConsentAdminTools =
        roles.includes("admin_role") || roles.includes("consent_role-admin");

    const canUseConsentTools =
        canUseConsentAdminTools || roles.includes("consent_role");

    const canUseBatchUpload =
        canUseConsentAdminTools ||
        roles.includes("consent_role-batch-initiation-uploader");

    return (
        <div className="layout">
            <aside className="left-bar">
                <ul>
                    {roles.includes("admin_role") && (
                        <li>
                            <Link to="/UserControl">
                                <ManageAccountsIcon style={{ color: "black", fontSize: 24 }} />
                            </Link>
                        </li>
                    )}

                    <li>
                        <Link to="/dashboard">
                            <HomeIcon style={{ color: "black", fontSize: 24 }} />
                        </Link>
                    </li>

                    {selectedAction === "Monthly check in" && (
                        <li>
                            <span
                                className="material-symbols-outlined"
                                style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                onClick={() => setOpenMonthlyReportModal(true)}
                            >
                                lab_profile
                            </span>
                        </li>
                    )}

                    {selectedAction === "Consent" && (
                        <>
                            {canUseConsentAdminTools && (
                                <li>
                                    <QueryStatsIcon
                                        titleAccess="Patient Consent Stats"
                                        style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                        onClick={() => setOpenPatientConsentStats(true)}
                                    />
                                </li>
                            )}

                            {canUseConsentAdminTools && (
                                <li>
                                    <PersonSearchIcon
                                        titleAccess="Patient ID Lookup"
                                        aria-label="Open Patient ID Lookup"
                                        role="button"
                                        tabIndex={0}
                                        style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                        onClick={() => setOpenPatientIdLookup(true)}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                setOpenPatientIdLookup(true);
                                            }
                                        }}
                                    />
                                </li>
                            )}

                            {canUseBatchUpload && (
                                <li>
                                    <CloudUploadIcon
                                        titleAccess="Batch Upload"
                                        style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                        onClick={() => setOpenBatchUpload(true)}
                                    />
                                </li>
                            )}

                            {canUseConsentTools && (
                                <li>
                                    <DashboardIcon
                                        titleAccess="External Patient Consent Dashboard"
                                        style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                        onClick={() => setOpenExternalConsentDashboard(true)}
                                    />
                                </li>
                            )}

                            {canUseConsentTools && (
                                <li>
                                    <a
                                        href="/consent/external-consent-form"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        title="External Consent Form"
                                        style={{
                                            color: "black",
                                            fontSize: 12,
                                            textDecoration: "none",
                                            cursor: "pointer",
                                        }}
                                    >
                                        External Consent Form
                                    </a>
                                </li>
                            )}
                        </>
                    )}

                    {selectedAction === "Opt out" && (
                        <>
                            <li>
                                <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                    onClick={() => setOpenOptOutReportModal(true)}
                                >
                                    lab_profile
                                </span>
                            </li>

                            <li>
                                <span
                                    className="material-symbols-outlined"
                                    title="History"
                                    style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                    onClick={() => setShowLog(true)}
                                    aria-label="Open History"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) =>
                                        (event.key === "Enter" || event.key === " ") &&
                                        setShowLog(true)
                                    }
                                >
                                    history
                                </span>
                            </li>

                            <li>
                                <span
                                    className="material-symbols-outlined"
                                    style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                    onClick={() => setOpenUploadModal(true)}
                                >
                                    upload_file
                                </span>
                            </li>
                        </>
                    )}

                    {selectedAction === "Facility Management" && (
                        <>
                            <li>
                                <span
                                    className="material-symbols-outlined"
                                    title="Process Changes"
                                    style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                    onClick={() => setOpenProcessChanges(true)}
                                    aria-label="Open Process Changes"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            setOpenProcessChanges(true);
                                        }
                                    }}
                                >
                                    published_with_changes
                                </span>
                            </li>

                            <li>
                                <BlockIcon
                                    titleAccess="View Exclusions"
                                    style={{ fontSize: 24, color: "black", cursor: "pointer" }}
                                    onClick={() => setOpenExclusions(true)}
                                />
                            </li>
                        </>
                    )}
                </ul>
            </aside>

            <div className="main-content">
                <nav className="top-bar">
                    <div className="top-bar-left">
                        <img
                            src="2023 Tapestry Logo-400x80.png"
                            alt="Tapestry Logo"
                            className="logo"
                        />
                    </div>

                    <div className="top-bar-center">
                        <p className="center-text">
                            {pageTitleMap[selectedAction] || "Management System"}
                        </p>
                    </div>

                    <div className="top-bar-right">
                        <span className="helpdesk-support">
                            <span className="help-icon">?</span> IT Helpdesk Support
                        </span>

                        <div className="user-info">
                            <AccountCircleIcon style={{ fontSize: 42, color: "#2f5d90" }} />
                            <div>
                                {kc?.idTokenParsed?.given_name || "Guest"}{" "}
                                {kc?.idTokenParsed?.family_name || ""}
                                <div>({roles[0] || "user"})</div>
                            </div>
                        </div>

                        <button onClick={handleLogout} className="logout-button">
                            Logout
                        </button>
                    </div>
                </nav>

                <div className="content">
                    <Outlet />
                </div>
            </div>

            <footer className="bottom-bar">
                <p>TapestryHealth 2025</p>
            </footer>

            <ConsentModal
                isOpen={consentOpen}
                onClose={closeConsentModal}
                mode={consentMode}
                context={consentContext}
            />

            <MonthlyCheckinRunReport
                open={openMontlyReportModal}
                onClose={() => setOpenMonthlyReportModal(false)}
            />

            <OptOutReportModal
                open={openOptOutReportModal}
                onClose={() => setOpenOptOutReportModal(false)}
            />

            <UploadSpreadsheetModal
                open={openUploadModal}
                onClose={() => setOpenUploadModal(false)}
            />

            <LogModal open={showLog} onClose={() => setShowLog(false)} />

            <ProcessChanges
                open={openProcessChanges}
                onClose={() => setOpenProcessChanges(false)}
            />

            <ExclusionsModal
                open={openExclusions}
                onClose={() => setOpenExclusions(false)}
            />

            <PatientConsentStatsModal
                open={openPatientConsentStats}
                onClose={() => setOpenPatientConsentStats(false)}
            />

            <PatientIdLookupModal
                open={openPatientIdLookup}
                onClose={() => setOpenPatientIdLookup(false)}
                token={kc?.token}
            />

            <ExternalPatientConsentDashboardModal
                open={openExternalConsentDashboard}
                onClose={() => setOpenExternalConsentDashboard(false)}
            />

            <BatchUploadModal
                open={openBatchUpload}
                onClose={() => setOpenBatchUpload(false)}
            />
        </div>
    );
};

export default Layout;
